/**
 * The composition root.
 *
 * The store, the sync engine and the API client are built here, once, and
 * handed to the app as one object. Nothing below this file constructs any of
 * them — that is what keeps Dexie and `fetch` out of the screens.
 *
 * ── Sync is TWO directions, and both are wired here ──────────────────────────
 *
 * `drain()` pushes what the device owes the office. `pullAssignments()` brings
 * down the work the office has handed to this surveyor — spec §2.1's "Assigned
 * (usual)", which is the PRIMARY way a survey comes into being; "+ New Survey"
 * is the exception. For a long time only the push half was wired: `pull()`
 * existed and was called from nothing, so a survey Chip or Tim assigned in the
 * office never reached the phone at all. Every E2E test was green because every
 * test created its own survey locally.
 *
 * `syncNow()` therefore does both, and PULLS FIRST. A device coming back into
 * signal with forty photos queued would otherwise not see a new assignment until
 * the last of them had uploaded; the incoming job is the thing the surveyor is
 * standing there waiting for, and it costs one request.
 *
 * Triggers, and why each one exists:
 *
 *   start()                — recovers rows a killed tab left `in_flight` and
 *                            publishes real counters to the pill (§6.3).
 *   connectivity events    — reconnecting kicks a sync immediately; that is the
 *                            whole point of the signal.
 *   visibilitychange       — the surveyor walking out of the machine room and
 *                            looking at their phone is the most reliable "we
 *                            might have bars now" event iOS gives us.
 *   30s interval           — the backstop. `dueOutbox` honours each row's
 *                            backoff, so a tick with nothing due costs one
 *                            indexed read, and the pull costs one request that
 *                            writes nothing when nothing has changed.
 *
 * The pull half is skipped entirely on an unlinked device: with no token there
 * is nobody to have assigned anything, and asking would put a credential error
 * in the sync pill of a phone whose only real problem is that it has not been
 * texted a link yet.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './ui/theme.css';
import { App } from './ui/App';
import { registerAppUpdate } from './ui/appUpdate';
import { AdminClient } from './db/admin-client';
import { ApiClient } from './db/http-transport';
import { SurveyStore } from './db/store';
import { SyncEngine, attachBrowserConnectivity } from './db/sync';
import { attachWakeLock } from './ui/wakeLock';
import { consoleLogSink } from './core/log';
import { claimTokenFromLocation, deviceId, readToken } from './ui/state/identity';
import { claimOnboardingFromLocation } from './ui/state/onboardingClaim';
import { readOfficeKey } from './ui/state/officeKey';

const SYNC_INTERVAL_MS = 30_000;

// Claim a token out of the assignment link before anything else renders, so the
// first `/api/me` and the first drain both have it, and so the credential is out
// of the URL before any screen — or any screenshot of the address bar — exists.
const { token } = claimTokenFromLocation();

// A scanned onboarding QR arrives as `#o=<secret>` (D-013). Same treatment and
// for the same reason: the secret comes out of the address bar before any
// screen — or any screenshot of the address bar — exists, and the app is sent
// to `#/onboard`, which spends it. Runs AFTER the token claim because the two
// parameters are mutually exclusive and `claimTokenFromLocation` leaves a URL
// carrying only `o=` untouched.
claimOnboardingFromLocation();

// Round 8.1, task 1: registers the service worker itself (injectRegister:
// false in vite.config.ts) and reloads onto a new build once it is safe to —
// see src/ui/appUpdate.ts.
registerAppUpdate();

const store = SurveyStore.open();
// Ask the browser not to evict this origin's storage under pressure (round 8,
// task 4). Photo bytes live in IndexedDB and iOS clears them first; a lost
// picture can now be fetched back from R2, but the ask is one call and it
// keeps the bytes from going in the first place. Best effort: a browser that
// refuses, or has no such API, changes nothing.
void navigator.storage?.persist?.();
const api = new ApiClient({
  // The token is read fresh on every request rather than captured, so a device
  // re-linked from a new SMS starts working without a reload.
  token: readToken,
  deviceId: deviceId(),
});
// The office key lives in sessionStorage and is usually absent — on a field
// phone it always is, and every admin call then fails closed before it is sent.
const admin = new AdminClient({ key: readOfficeKey });
const sync = new SyncEngine(store, {
  transport: api,
  deviceId: deviceId(),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
});

/**
 * One full sync: pull the inbox, then push the outbox.
 *
 * Sequential and in that order on purpose (see the header). Neither half can
 * reject — a failed pull and a failed drain both leave the device exactly as it
 * was, and both are already reported through the sync pill.
 */
async function syncNow(): Promise<void> {
  // `readToken()` rather than the captured `token`: a device linked mid-session
  // from a second tab starts pulling on the next tick, with no reload.
  if (readToken() !== null) {
    await sync.pullAssignments().catch(() => undefined);
  }
  await sync.drain().catch(() => undefined);
}

void sync
  .start()
  .then(() => syncNow())
  .catch(() => undefined);
attachBrowserConnectivity(sync);

// The phone stays awake while it still owes the office something (D-052 §1).
// Every trigger below runs inside the page, and iOS stops the page the moment
// the screen locks — Grand Dunes sat two photos and a seal short all night
// that way, with the app "open". `readToken()` again rather than the captured
// `token`, for the reason `syncNow` gives.
attachWakeLock(sync, { navigator, document, linked: () => readToken() !== null });

// `attachBrowserConnectivity` already kicks a DRAIN on reconnect; this is the
// pull half of the same event. Both are guarded against re-entry, so the two
// landing together costs nothing.
window.addEventListener('online', () => {
  void syncNow();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void syncNow();
});

window.setInterval(() => {
  void syncNow();
}, SYNC_INTERVAL_MS);

const container = document.getElementById('root');
if (!container) throw new Error('main: #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App services={{ store, sync, api, admin, linked: token !== null, log: consoleLogSink }} />
  </StrictMode>,
);
